import React from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Stack, IconButton, CircularProgress, Box } from '@mui/material';
import { Button } from 'components/index';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { LineItemUnitEnum } from '../../glossary/glossary.type';
import { Close } from '@mui/icons-material';
import SelectInput from '../../SelectInput';
import { ProjectLineItemDTO } from '../project.type';
import { CreateDrawLineItemDTO } from './project-draws.type';
import SelectDivision from '../../glossary/SelectDivision';
import NumericFormatInput from '../../general/NumericFormatInput';
import Autocomplete from '@mui/material/Autocomplete';
import { useLineItemSearch } from '../../../hooks/useLineItemSearch';

interface EditPayappDrawlineitemDialogProps {
  open: boolean;
  onClose: () => void;
  projectDrawId: string;
  lineItem: ProjectLineItemDTO | null;
  setValues: (values: CreateDrawLineItemDTO) => void;
}

const unitsList = Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit }));

const EditPayappDrawlineitemDialog: React.FC<EditPayappDrawlineitemDialogProps> = ({
  open,
  onClose,
  projectDrawId,
  lineItem,
  setValues,
}) => {
  const { lineItemOptions, isSearching, searchLineItems } = useLineItemSearch(open);
  const [dragPosition, setDragPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });

  const handleMouseDown = (event: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: event.clientX - dragPosition.x,
      y: event.clientY - dragPosition.y,
    });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (isDragging) {
      setDragPosition({
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (event: MouseEvent) => {
        setDragPosition({
          x: event.clientX - dragStart.x,
          y: event.clientY - dragStart.y,
        });
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  // Reset position when dialog opens
  React.useEffect(() => {
    if (open) {
      setDragPosition({ x: 0, y: 0 });
    }
  }, [open]);

  const initialFormValues = {
    id: lineItem?.id || '',
    projectDrawId: lineItem?.projectDrawId || projectDrawId,
    description: lineItem?.lineItem.description || '',
    unit: lineItem?.lineItem.unit || unitsList[0].value,
    divisionId: lineItem?.lineItem.division?.id || null,
    originalBudget: lineItem?.originalBudget || 0,
    changeOrder: lineItem?.changeOrder || 0,
    reallocation: lineItem?.reallocation || 0,
    previousApplication: lineItem?.previousApplication || 0,
    completeThisPeriod: lineItem?.completeThisPeriod || 0,
    storedThisPeriod: lineItem?.storedThisPeriod || 0,
    quantity: lineItem?.quantity || 0,
    retainage: lineItem?.retainage || 0,
  };

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      description: yup.string().required('Description is required'),
      unit: yup.string().required('Unit is required'),
      originalBudget: yup.number().required('Budget is required'),
      changeOrder: yup.number().required('Change Order is required'),
      reallocation: yup.number().required('Reallocation is required'),
      previousApplication: yup.number().required('Previous Application is required'),
      completeThisPeriod: yup.number().required('Complete This Period is required'),
      storedThisPeriod: yup.number().required('Stored This Period is required'),
      quantity: yup.number().required('Quantity is required'),
      retainage: yup.number().required('Retainage is required'),
    }),
    onSubmit: async values => {
      setValues({ ...values });
      formik.resetForm();
      onClose();
    },
  });

  // Calculate derived values
  const derivedValues = React.useMemo(() => {
    const revisedBudget = (formik.values.originalBudget || 0) + (formik.values.reallocation || 0) + (formik.values.changeOrder || 0);

    // If the user has changed CTP or STP, cdccApproved will be reset to CTP+STP on save.
    // If they haven't changed them, the existing cdccApproved (possibly a manual override) is preserved.
    const ctpChanged = formik.values.completeThisPeriod !== (lineItem?.completeThisPeriod || 0);
    const stpChanged = formik.values.storedThisPeriod !== (lineItem?.storedThisPeriod || 0);
    const effectiveCdccApproved =
      ctpChanged || stpChanged
        ? (formik.values.completeThisPeriod || 0) + (formik.values.storedThisPeriod || 0)
        : (lineItem?.cdccApproved ?? (formik.values.completeThisPeriod || 0) + (formik.values.storedThisPeriod || 0));

    const completedStoredToDate = (formik.values.previousApplication || 0) + effectiveCdccApproved;
    const percentageComplete = revisedBudget !== 0 ? (completedStoredToDate / revisedBudget) * 100 : 0;
    const balanceToComplete = revisedBudget - completedStoredToDate;

    return {
      revisedBudget,
      completedStoredToDate,
      percentageComplete,
      balanceToComplete,
    };
  }, [
    formik.values.originalBudget,
    formik.values.reallocation,
    formik.values.changeOrder,
    formik.values.previousApplication,
    formik.values.completeThisPeriod,
    formik.values.storedThisPeriod,
    lineItem?.cdccApproved,
    lineItem?.completeThisPeriod,
    lineItem?.storedThisPeriod,
  ]);

  const renderGlossaryInputs = () => {
    if (lineItem) return null;
    return (
      <>
        <Autocomplete
          freeSolo
          filterOptions={() => lineItemOptions}
          options={lineItemOptions}
          getOptionLabel={option => {
            if (typeof option === 'string') return option;
            return option.description || '';
          }}
          loading={isSearching}
          value={formik.values.description}
          onChange={(event, newValue) => {
            const description = typeof newValue === 'string' ? newValue : newValue?.description || '';
            formik.setFieldValue('description', description);
          }}
          onInputChange={(event, newInputValue) => {
            formik.setFieldValue('description', newInputValue);
            searchLineItems(newInputValue);
          }}
          id='description-autocomplete'
          renderInput={params => (
            <TextField
              {...params}
              label='Description'
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
              fullWidth
              variant='standard'
              InputLabelProps={{ shrink: true }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearching ? <CircularProgress color='inherit' size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const displayText = typeof option === 'string' ? option : option.description || '';
            const key = typeof option === 'string' ? option : option.id || option.description || '';
            return (
              <li {...props} key={key}>
                {displayText}
              </li>
            );
          }}
        />

        <SelectDivision
          selectedDivision={formik.values.divisionId}
          setSelectedDivision={(divisionId: string | null) => formik.setFieldValue('divisionId', divisionId)}
        />

        <SelectInput label='Unit' selected={formik.values.unit} setSelected={unit => formik.setFieldValue('unit', unit)} list={unitsList} />
      </>
    );
  };

  const readOnlyFieldStyle = {
    '& .MuiInputBase-input': {
      color: 'rgba(0, 0, 0, 0.38)',
      WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
      cursor: 'not-allowed',
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: {
          minWidth: '1200px',
          position: 'relative',
          transform: `translate(${dragPosition.x}px, ${dragPosition.y}px)`,
          transition: isDragging ? 'none' : 'transform 0.2s ease-out',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {lineItem ? `Edit Line Item : ${lineItem.lineItem.description}` : 'Add Line Item'}
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2} py={2}>
            {renderGlossaryInputs()}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 1fr)',
                gridTemplateRows: 'auto auto',
                gap: '24px',
                '& > *': { minWidth: 0 },
              }}
            >
              {/* First Row - 6 fields */}
              <Box sx={{ gridColumn: '1 / 2', gridRow: '1 / 2' }}>
                <TextField
                  label='Original Budget'
                  fullWidth
                  variant='standard'
                  value={formik.values.originalBudget}
                  onChange={formik.handleChange}
                  name='originalBudget'
                  error={formik.touched.originalBudget && Boolean(formik.errors.originalBudget)}
                  helperText={formik.touched.originalBudget && formik.errors.originalBudget}
                  InputProps={{ inputComponent: NumericFormatInput as any }}
                />
              </Box>

              <Box sx={{ gridColumn: '2 / 3', gridRow: '1 / 2' }}>
                <TextField
                  label='Change Order'
                  fullWidth
                  variant='standard'
                  value={formik.values.changeOrder}
                  onChange={formik.handleChange}
                  name='changeOrder'
                  error={formik.touched.changeOrder && Boolean(formik.errors.changeOrder)}
                  helperText={formik.touched.changeOrder && formik.errors.changeOrder}
                  InputProps={{ inputComponent: NumericFormatInput as any }}
                />
              </Box>

              <Box sx={{ gridColumn: '3 / 4', gridRow: '1 / 2' }}>
                <TextField
                  label='Reallocation'
                  fullWidth
                  variant='standard'
                  value={formik.values.reallocation}
                  onChange={formik.handleChange}
                  name='reallocation'
                  error={formik.touched.reallocation && Boolean(formik.errors.reallocation)}
                  helperText={formik.touched.reallocation && formik.errors.reallocation}
                  InputProps={{ inputComponent: NumericFormatInput as any }}
                />
              </Box>

              <Box sx={{ gridColumn: '4 / 5', gridRow: '1 / 2' }}>
                <TextField
                  label='Revised Budget'
                  fullWidth
                  variant='standard'
                  value={derivedValues.revisedBudget}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                    readOnly: true,
                  }}
                  sx={readOnlyFieldStyle}
                />
              </Box>

              <Box sx={{ gridColumn: '5 / 6', gridRow: '1 / 2' }}>
                <TextField
                  label='Previous Application'
                  fullWidth
                  variant='standard'
                  value={formik.values.previousApplication}
                  onChange={formik.handleChange}
                  name='previousApplication'
                  error={formik.touched.previousApplication && Boolean(formik.errors.previousApplication)}
                  helperText={formik.touched.previousApplication && formik.errors.previousApplication}
                  InputProps={{ inputComponent: NumericFormatInput as any }}
                />
              </Box>

              <Box sx={{ gridColumn: '6 / 7', gridRow: '1 / 2' }}>
                <TextField
                  label='Complete This Period'
                  fullWidth
                  variant='standard'
                  value={formik.values.completeThisPeriod}
                  onChange={formik.handleChange}
                  name='completeThisPeriod'
                  error={formik.touched.completeThisPeriod && Boolean(formik.errors.completeThisPeriod)}
                  helperText={formik.touched.completeThisPeriod && formik.errors.completeThisPeriod}
                  InputProps={{ inputComponent: NumericFormatInput as any }}
                />
              </Box>

              {/* Second Row - 5 fields + 1 empty cell */}
              <Box sx={{ gridColumn: '1 / 2', gridRow: '2 / 3' }}>
                <TextField
                  label='Stored This Period'
                  fullWidth
                  variant='standard'
                  value={formik.values.storedThisPeriod}
                  onChange={formik.handleChange}
                  name='storedThisPeriod'
                  error={formik.touched.storedThisPeriod && Boolean(formik.errors.storedThisPeriod)}
                  helperText={formik.touched.storedThisPeriod && formik.errors.storedThisPeriod}
                  InputProps={{ inputComponent: NumericFormatInput as any }}
                />
              </Box>

              <Box sx={{ gridColumn: '2 / 3', gridRow: '2 / 3' }}>
                <TextField
                  label='Completed Stored To Date'
                  fullWidth
                  variant='standard'
                  value={derivedValues.completedStoredToDate}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                    readOnly: true,
                  }}
                  sx={readOnlyFieldStyle}
                />
              </Box>

              <Box sx={{ gridColumn: '3 / 4', gridRow: '2 / 3' }}>
                <TextField
                  label='Percentage Complete'
                  fullWidth
                  variant='standard'
                  value={derivedValues.percentageComplete}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                    readOnly: true,
                  }}
                  sx={readOnlyFieldStyle}
                />
              </Box>

              <Box sx={{ gridColumn: '4 / 5', gridRow: '2 / 3' }}>
                <TextField
                  label='Balance To Complete'
                  fullWidth
                  variant='standard'
                  value={derivedValues.balanceToComplete}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                    readOnly: true,
                  }}
                  sx={readOnlyFieldStyle}
                />
              </Box>

              <Box sx={{ gridColumn: '5 / 6', gridRow: '2 / 3' }}>
                <TextField
                  label='Retainage'
                  fullWidth
                  variant='standard'
                  value={formik.values.retainage}
                  onChange={formik.handleChange}
                  name='retainage'
                  error={formik.touched.retainage && Boolean(formik.errors.retainage)}
                  helperText={formik.touched.retainage && formik.errors.retainage}
                  InputProps={{ inputComponent: NumericFormatInput as any }}
                />
              </Box>

              {/* Empty cell for alignment */}
              <Box sx={{ gridColumn: '6 / 7', gridRow: '2 / 3' }} />
            </Box>
          </Stack>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <Button variant='outlined' color='error' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='contained' onClick={() => formik.submitForm()} disabled={!formik.isValid || !formik.dirty}>
          {lineItem ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPayappDrawlineitemDialog;
